import {
  PaidMonetizationControl,
  BillingProduct,
  CanonicalSubscription,
  PaymentTransaction,
  BillingInvoice,
  BillingCoupon,
  ProviderCapability,
  BillingReconciliationItem
} from '../services/billing/billingTypes';

export const INITIAL_MONETIZATION_CONTROL: PaidMonetizationControl = {
  mode: 'OFF', // DEFAULT OFF BY POLICY
  emergencyKillSwitch: false,
  checkoutEnabled: false,
  subscriptionsEnabled: false,
  usageBillingEnabled: false,
  couponsEnabled: true,
  trialsEnabled: true,
  activeRolloutMarkets: ['US', 'CA', 'GB', 'FR', 'DE', 'CI', 'SN'],
  lastChangedAt: '2026-08-25T00:00:00Z',
  lastChangedBy: 'Chief Revenue Officer'
};

export const BILLING_EXECUTIVE_METRICS = {
  activePaidSubscribers: '412',
  mrrUsd: '$18,450',
  arrUsd: '$221,400',
  paymentSuccessRatePct: 99.4,
  activeInvoicesCount: '620',
  refundRatePct: 0.12,
  disputeRatePct: 0.02,
  reconciledLedgerPct: 100.0,
  averageSettlementTimeHours: 24,
  readinessScorecardPct: 96.5
};

export const BILLING_PRODUCTS: BillingProduct[] = [
  {
    id: 'prod-yamoo-pro',
    key: 'yamoo_pro_individual',
    name: 'YamooPay Pro (Financial Power User)',
    description: 'Unlimited saved financial scenarios, custom compound interest schedules, inflation alerts, and zero platform advertisements.',
    productType: 'SUBSCRIPTION',
    status: 'ACTIVE',
    monthlyBasePriceUsd: 9.99,
    annualBasePriceUsd: 99.00,
    includedComputeUnits: 0,
    entitlements: ['saved_scenarios.unlimited', 'ads.disabled', 'alerts.advanced_push', 'fire_retirement.export_pdf'],
    supportedProviders: ['STRIPE', 'MONEROO', 'CHARIOW']
  },
  {
    id: 'prod-dev-starter',
    key: 'api_developer_starter',
    name: 'Developer API Starter',
    description: '1,000,000 monthly compute units, 600 req/min burst limits, verified mid-market FX, and salary tax endpoints.',
    productType: 'USAGE_BASED',
    status: 'ACTIVE',
    monthlyBasePriceUsd: 49.00,
    annualBasePriceUsd: 490.00,
    includedComputeUnits: 1000000,
    entitlements: ['api.monthly_units.1m', 'api.rate_limit.600_rpm', 'webhooks.endpoints.5', 'fx.live_reference'],
    supportedProviders: ['STRIPE']
  },
  {
    id: 'prod-dev-growth',
    key: 'api_developer_growth',
    name: 'Developer API Growth & High-Throughput',
    description: '10,000,000 monthly compute units, 2,500 req/min rate limit, priority SLAs, signed webhooks, and white-label widgets.',
    productType: 'HYBRID',
    status: 'ACTIVE',
    monthlyBasePriceUsd: 249.00,
    annualBasePriceUsd: 2490.00,
    includedComputeUnits: 10000000,
    entitlements: ['api.monthly_units.10m', 'api.rate_limit.2500_rpm', 'widget.white_label', 'support.priority_sla'],
    supportedProviders: ['STRIPE']
  },
  {
    id: 'prod-widget-whitelabel',
    key: 'embeddable_widget_whitelabel',
    name: 'White-Label Financial Widget License',
    description: 'Remove YamooPay attribution, inject custom brand styling tokens, and host on unlimited partner domains.',
    productType: 'SUBSCRIPTION',
    status: 'ACTIVE',
    monthlyBasePriceUsd: 79.00,
    annualBasePriceUsd: 790.00,
    includedComputeUnits: 0,
    entitlements: ['widget.white_label', 'widget.custom_tokens', 'widget.unlimited_domains'],
    supportedProviders: ['STRIPE', 'MONEROO', 'CHARIOW']
  }
];

export const MOCK_SUBSCRIPTIONS: CanonicalSubscription[] = [
  {
    id: 'sub_99a8b1c4',
    customerId: 'cust_acme_corp',
    customerEmail: 'billing@acmewealth.com',
    productId: 'prod-dev-growth',
    productName: 'Developer API Growth & High-Throughput',
    planInterval: 'MONTHLY',
    status: 'ACTIVE',
    provider: 'STRIPE',
    providerSubscriptionId: 'sub_stripe_1Nx882A',
    currentPeriodStart: '2026-08-01T00:00:00Z',
    currentPeriodEnd: '2026-08-31T23:59:59Z',
    cancelAtPeriodEnd: false,
    amountFormatted: '$249.00 / mo'
  },
  {
    id: 'sub_77d2e99f',
    customerId: 'cust_realty_partner',
    customerEmail: 'ops@luxuryhomes.com',
    productId: 'prod-widget-whitelabel',
    productName: 'White-Label Financial Widget License',
    planInterval: 'ANNUAL',
    status: 'ACTIVE',
    provider: 'MONEROO',
    providerSubscriptionId: 'mon_sub_5510b9',
    currentPeriodStart: '2026-02-15T00:00:00Z',
    currentPeriodEnd: '2027-02-14T23:59:59Z',
    cancelAtPeriodEnd: false,
    amountFormatted: '$790.00 / yr'
  },
  {
    id: 'sub_12c044e1',
    customerId: 'cust_individual_power',
    customerEmail: 'sarah.miller@investor.io',
    productId: 'prod-yamoo-pro',
    productName: 'YamooPay Pro (Financial Power User)',
    planInterval: 'MONTHLY',
    status: 'ACTIVE',
    provider: 'STRIPE',
    providerSubscriptionId: 'sub_stripe_9921kL',
    currentPeriodStart: '2026-08-10T00:00:00Z',
    currentPeriodEnd: '2026-09-09T23:59:59Z',
    cancelAtPeriodEnd: false,
    amountFormatted: '$9.99 / mo'
  }
];

export const MOCK_PAYMENT_TRANSACTIONS: PaymentTransaction[] = [
  {
    id: 'txn_881920_str',
    customerId: 'cust_acme_corp',
    customerEmail: 'billing@acmewealth.com',
    amount: 249.00,
    currency: 'USD',
    provider: 'STRIPE',
    providerTransactionId: 'pi_3Pj8821098df23',
    status: 'SUCCEEDED',
    paymentMethodSummary: 'Visa ending in 4242',
    createdAt: '2026-08-01T00:05:12Z',
    invoiceId: 'inv_2026_08_001',
    isRefunded: false
  },
  {
    id: 'txn_551098_mon',
    customerId: 'cust_realty_partner',
    customerEmail: 'ops@luxuryhomes.com',
    amount: 790.00,
    currency: 'USD',
    provider: 'MONEROO',
    providerTransactionId: 'mon_pay_9941a8',
    status: 'SUCCEEDED',
    paymentMethodSummary: 'Mobile Money (Orange / MTN)',
    createdAt: '2026-02-15T10:14:00Z',
    invoiceId: 'inv_2026_02_094',
    isRefunded: false
  },
  {
    id: 'txn_339102_cha',
    customerId: 'cust_saas_lab',
    customerEmail: 'founder@fintechlab.fr',
    amount: 49.00,
    currency: 'EUR',
    provider: 'CHARIOW',
    providerTransactionId: 'cha_order_7719',
    status: 'SUCCEEDED',
    paymentMethodSummary: 'Mastercard ending in 8812',
    createdAt: '2026-08-20T14:30:22Z',
    invoiceId: 'inv_2026_08_042',
    isRefunded: false
  }
];

export const MOCK_INVOICES: BillingInvoice[] = [
  {
    id: 'inv_2026_08_001',
    invoiceNumber: 'YP-INV-2026-0849',
    customerId: 'cust_acme_corp',
    customerEmail: 'billing@acmewealth.com',
    amountDue: 249.00,
    amountPaid: 249.00,
    currency: 'USD',
    status: 'PAID',
    createdAt: '2026-08-01T00:00:00Z',
    paidAt: '2026-08-01T00:05:12Z',
    items: [
      { description: 'Developer API Growth Plan (August 2026)', amount: 249.00, quantity: 1 }
    ]
  },
  {
    id: 'inv_2026_08_042',
    invoiceNumber: 'YP-INV-2026-0850',
    customerId: 'cust_saas_lab',
    customerEmail: 'founder@fintechlab.fr',
    amountDue: 49.00,
    amountPaid: 49.00,
    currency: 'EUR',
    status: 'PAID',
    createdAt: '2026-08-20T14:28:00Z',
    paidAt: '2026-08-20T14:30:22Z',
    items: [
      { description: 'Developer API Starter Monthly', amount: 49.00, quantity: 1 }
    ]
  }
];

export const MOCK_COUPONS: BillingCoupon[] = [
  {
    id: 'coup-early-dev',
    code: 'BUILDER50',
    discountType: 'PERCENTAGE',
    discountValue: 50,
    maxRedemptions: 500,
    redemptionsCount: 142,
    expiresAt: '2026-12-31T23:59:59Z',
    status: 'ACTIVE'
  },
  {
    id: 'coup-annual-save',
    code: 'YAMOOANNUAL',
    discountType: 'FIXED_AMOUNT',
    discountValue: 20,
    maxRedemptions: 1000,
    redemptionsCount: 280,
    expiresAt: '2026-12-31T23:59:59Z',
    status: 'ACTIVE'
  }
];

export const PROVIDER_CAPABILITIES: ProviderCapability[] = [
  {
    provider: 'STRIPE',
    name: 'Stripe Billing & Payments',
    hostedCheckout: true,
    nativeSubscriptions: true,
    usageBasedMetering: true,
    customerPortal: true,
    refunds: true,
    mobileMoneySupported: false,
    supportedCurrencies: ['USD', 'EUR', 'GBP', 'CAD', 'AUD', 'JPY', 'CHF'],
    status: 'HEALTHY'
  },
  {
    provider: 'MONEROO',
    name: 'Moneroo Multi-Channel Aggregator',
    hostedCheckout: true,
    nativeSubscriptions: true,
    usageBasedMetering: false,
    customerPortal: false,
    refunds: true,
    mobileMoneySupported: true,
    supportedCurrencies: ['XOF', 'XAF', 'USD', 'EUR', 'NGN', 'GHS', 'KES'],
    status: 'HEALTHY'
  },
  {
    provider: 'CHARIOW',
    name: 'Chariow Checkout & Pulse',
    hostedCheckout: true,
    nativeSubscriptions: true,
    usageBasedMetering: false,
    customerPortal: false,
    refunds: true,
    mobileMoneySupported: true,
    supportedCurrencies: ['EUR', 'USD', 'XOF', 'XAF'],
    status: 'HEALTHY'
  }
];

export const MOCK_RECONCILIATION_LOGS: BillingReconciliationItem[] = [
  {
    id: 'rec_01',
    type: 'PAYMENT_STATUS_MISMATCH',
    provider: 'STRIPE',
    entityId: 'pi_test_9921',
    description: 'Simulated test invoice reconciled with Stripe settlement ledger. Zero discrepancies.',
    severity: 'MODERATE_P2',
    detectedAt: '2026-08-24T18:00:00Z',
    status: 'RESOLVED',
    resolutionAction: 'Automated background cron matched transaction ID with invoice settlement.'
  }
];
